import Box from '@mui/material/Box';
import { alpha, useTheme } from '@mui/material/styles';
import { DESKTOP_UP_MQ } from './loginOverlay.constants';

const CloseGlyph = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
		<path d="M1 1L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
		<path d="M21 1L1 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

type CloseButtonProps = {
	onClose: () => void;
	closeLabel: string;
	shouldReduceMotion: boolean;
	variant: 'mobile' | 'desktop';
};

const getIconAnimationSx = (shouldReduceMotion: boolean) => ({
	'& .login-overlay-close-icon': {
		transition: shouldReduceMotion ? 'none' : 'transform 360ms cubic-bezier(0.22, 1, 0.36, 1)'
	},
	'&:hover .login-overlay-close-icon': {
		transform: shouldReduceMotion ? 'none' : 'rotate(180deg)'
	}
});

const CloseButton = ({ onClose, closeLabel, shouldReduceMotion, variant }: CloseButtonProps) => {
	const theme = useTheme();

	const focusRingSx = {
		'&:focusVisible': {
			outline: `2px solid ${alpha(theme.palette.text.secondary, 0.45)}`,
			outlineOffset: 4
		}
	};

	if (variant === 'mobile') {
		return (
			<Box
				component="button"
				type="button"
				onClick={onClose}
				aria-label={closeLabel}
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					mt: { xs: '9px', sm: '11px' },
					color: 'text.secondary',
					background: 'none',
					border: 'none',
					padding: 0,
					cursor: 'pointer',
					...getIconAnimationSx(shouldReduceMotion),
					...focusRingSx
				}}
			>
				<Box component="span" className="login-overlay-close-icon" sx={{ display: 'flex' }}>
					<CloseGlyph />
				</Box>
			</Box>
		);
	}

	return (
		<Box
			component="button"
			type="button"
			onClick={onClose}
			aria-label={closeLabel}
			sx={{
				display: 'none',
				[DESKTOP_UP_MQ]: {
					display: 'flex',
					position: 'absolute',
					top: 40,
					right: 80,
					zIndex: 10,
					flexDirection: 'row',
					alignItems: 'center',
					gap: '20px',
					color: 'text.secondary',
					background: 'none',
					border: 'none',
					padding: 0,
					cursor: 'pointer',
					fontFamily: 'inherit',
					...getIconAnimationSx(shouldReduceMotion),
					...focusRingSx
				}
			}}
		>
			<Box
				component="span"
				sx={{
					typography: 'body-md',
					color: 'inherit',
					whiteSpace: 'nowrap',
					lineHeight: 1
				}}
			>
				{closeLabel}
			</Box>
			<Box component="span" className="login-overlay-close-icon" sx={{ display: 'flex', flexShrink: 0 }}>
				<CloseGlyph />
			</Box>
		</Box>
	);
};

export default CloseButton;
